import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle, AlertTriangle, ArrowRight, Zap, Target } from 'lucide-react';
import clsx from 'clsx';

export interface MappingConflict {
  outputPath: string;
  outputName: string;
  candidates: Array<{
    inputPath: string;
    inputName: string;
    certaintyScore?: number;
    mappingType: 'direct' | 'ai' | 'no-match';
    transformId?: string; // If this is an existing transform being updated
  }>;
}

export interface MappingSummaryData {
  totalProcessed: number;
  successfulMappings: number;
  conflicts: MappingConflict[];
  unmappedProperties: Array<{
    path: string;
    name: string;
    type: 'input' | 'output';
  }>;
  mappingType: 'auto' | 'ai';
}

interface MappingSummaryDialogProps {
  isOpen: boolean;
  summaryData: MappingSummaryData;
  onConfirm: (resolvedConflicts: { [outputPath: string]: string }) => void; // outputPath -> chosen inputPath
  onCancel: () => void;
}

const MappingSummaryDialog: React.FC<MappingSummaryDialogProps> = ({
  isOpen,
  summaryData,
  onConfirm,
  onCancel,
}) => {
  // State to track user's conflict resolutions
  const [conflictResolutions, setConflictResolutions] = useState<{ [outputPath: string]: string }>({});

  // Initialize conflict resolutions with the highest certainty score candidate
  React.useEffect(() => {
    if (summaryData.conflicts.length > 0) {
      const initialResolutions: { [outputPath: string]: string } = {};
      summaryData.conflicts.forEach(conflict => {
        // Choose the candidate with highest certainty score, or first direct match
        const bestCandidate = conflict.candidates.reduce((best, current) => {
          if (current.mappingType === 'direct' && best.mappingType !== 'direct') {
            return current;
          }
          if (current.mappingType === 'direct' && best.mappingType === 'direct') {
            return current; // Keep first direct match
          }
          if (current.mappingType !== 'direct' && best.mappingType === 'direct') {
            return best;
          }
          // Both are AI or no-match, compare certainty scores
          const currentScore = current.certaintyScore || 0;
          const bestScore = best.certaintyScore || 0;
          return currentScore > bestScore ? current : best;
        });
        initialResolutions[conflict.outputPath] = bestCandidate.inputPath;
      });
      setConflictResolutions(initialResolutions);
    }
  }, [summaryData.conflicts]);

  const handleConflictResolution = useCallback((outputPath: string, inputPath: string) => {
    setConflictResolutions(prev => ({
      ...prev,
      [outputPath]: inputPath
    }));
  }, []);

  const handleConfirm = useCallback(() => {
    onConfirm(conflictResolutions);
  }, [conflictResolutions, onConfirm]);

  const getMappingTypeIcon = (mappingType: string) => {
    switch (mappingType) {
      case 'direct':
        return <Target className="w-4 h-4 text-green-600" />;
      case 'ai':
        return <Zap className="w-4 h-4 text-blue-600" />;
      default:
        return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
    }
  };

  const getMappingTypeLabel = (mappingType: string) => {
    switch (mappingType) {
      case 'direct':
        return 'Exact Match';
      case 'ai':
        return 'AI Suggestion';
      default:
        return 'No Match';
    }
  };

  const getCertaintyColor = (score?: number) => {
    if (!score) return 'text-gray-500';
    if (score >= 0.8) return 'text-green-600';
    if (score >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (!isOpen) return null;

  const hasConflicts = summaryData.conflicts.length > 0;
  const allConflictsResolved = hasConflicts ? 
    summaryData.conflicts.every(conflict => conflictResolutions[conflict.outputPath]) : 
    true;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={onCancel}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          className="relative max-w-4xl w-full mx-4 bg-white dark:bg-gray-800 rounded-lg shadow-xl max-h-[80vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-6 h-6 text-green-500" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {summaryData.mappingType === 'ai' ? 'AI Mapping' : 'Auto Mapping'} Summary
                </h2>
              </div>
              <button
                onClick={onCancel}
                className="p-1 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4 max-h-96 overflow-y-auto">
            {/* Summary Stats */}
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Mapping Results</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div className="text-center">
                  <div className="text-lg font-semibold text-gray-900 dark:text-white">
                    {summaryData.totalProcessed}
                  </div>
                  <div className="text-gray-600 dark:text-gray-400">Total Processed</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-green-600 dark:text-green-400">
                    {summaryData.successfulMappings}
                  </div>
                  <div className="text-gray-600 dark:text-gray-400">Successful</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-yellow-600 dark:text-yellow-400">
                    {summaryData.conflicts.length}
                  </div>
                  <div className="text-gray-600 dark:text-gray-400">Conflicts</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-gray-600 dark:text-gray-400">
                    {summaryData.unmappedProperties.length}
                  </div>
                  <div className="text-gray-600 dark:text-gray-400">Unmapped</div>
                </div>
              </div>
            </div>

            {/* Conflicts Section */}
            {hasConflicts && (
              <div className="mb-6">
                <div className="flex items-center mb-4">
                  <AlertTriangle className="w-5 h-5 text-yellow-500 mr-2" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Resolve Mapping Conflicts
                  </h3>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Multiple input properties were mapped to the same output property. Please choose which mapping to keep for each conflict.
                </p>
                
                <div className="space-y-4">
                  {summaryData.conflicts.map((conflict) => (
                    <div key={conflict.outputPath} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                      <div className="mb-3">
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          Output Property: <span className="font-mono text-blue-600 dark:text-blue-400">{conflict.outputName}</span>
                        </h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">{conflict.outputPath}</p>
                      </div>
                      
                      <div className="space-y-2">
                        {conflict.candidates.map((candidate, candidateIndex) => (
                          <label
                            key={`${conflict.outputPath}-${candidate.inputPath}-${candidateIndex}`}
                            className={clsx(
                              "flex items-center p-3 rounded-md border cursor-pointer transition-colors",
                              conflictResolutions[conflict.outputPath] === candidate.inputPath
                                ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                                : "border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                            )}
                          >
                            <input
                              type="radio"
                              name={`conflict-${conflict.outputPath}`}
                              value={candidate.inputPath}
                              checked={conflictResolutions[conflict.outputPath] === candidate.inputPath}
                              onChange={() => handleConflictResolution(conflict.outputPath, candidate.inputPath)}
                              className="mr-3"
                            />
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                  {getMappingTypeIcon(candidate.mappingType)}
                                  <span className="font-medium text-gray-900 dark:text-white">
                                    {candidate.inputName}
                                  </span>
                                  <ArrowRight className="w-4 h-4 text-gray-400" />
                                  <span className="text-gray-600 dark:text-gray-400">
                                    {conflict.outputName}
                                  </span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className="text-xs px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                                    {getMappingTypeLabel(candidate.mappingType)}
                                  </span>
                                  {candidate.certaintyScore !== undefined && (
                                    <span className={clsx("text-xs font-medium", getCertaintyColor(candidate.certaintyScore))}>
                                      {Math.round(candidate.certaintyScore * 100)}%
                                    </span>
                                  )}
                                </div>
                              </div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 font-mono mt-1">
                                {candidate.inputPath}
                              </p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Unmapped Properties */}
            {summaryData.unmappedProperties.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Unmapped Properties ({summaryData.unmappedProperties.length})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {summaryData.unmappedProperties.slice(0, 10).map((prop, index) => (
                    <div key={`${prop.type}-${prop.path}-${index}`} className="text-sm text-gray-600 dark:text-gray-400 font-mono">
                      <span className={clsx(
                        "inline-block w-2 h-2 rounded-full mr-2",
                        prop.type === 'input' ? "bg-blue-400" : "bg-green-400"
                      )}></span>
                      {prop.name}
                    </div>
                  ))}
                  {summaryData.unmappedProperties.length > 10 && (
                    <div className="text-sm text-gray-500 dark:text-gray-400 italic">
                      ... and {summaryData.unmappedProperties.length - 10} more
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Success Message */}
            {!hasConflicts && summaryData.successfulMappings > 0 && (
              <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                  <h3 className="text-sm font-medium text-green-800 dark:text-green-200">
                    Mapping completed successfully!
                  </h3>
                </div>
                <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                  {summaryData.successfulMappings} properties were mapped without conflicts.
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!allConflictsResolved}
              className={clsx(
                "px-4 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-colors",
                allConflictsResolved
                  ? "bg-blue-600 hover:bg-blue-700 text-white"
                  : "bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
              )}
            >
              Apply Mappings
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default MappingSummaryDialog;
